import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Box, styled} from '@mui/material';
import {Body, designSystemToken, Label} from '@lightricks/react-design-system';

const DEFAULT_SETTINGS = {
  backgroundColor: undefined,
  borderRadius: undefined,
  optionColor: undefined,
  selectedOptionColor: undefined,
  optionBorderRadius: undefined,
};

export interface ToggleButtonOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
  minWidth?: string;

  flex?: number;
}

export type ToggleButtonLayout = 'equal' | 'content' | 'custom';

interface Settings {
  backgroundColor?: string;
  borderRadius?: string;
  optionColor?: string;
  selectedOptionColor?: string;
  optionBorderRadius?: string;
}

interface ToggleButtonProps {
  testID?: string;
  options: ToggleButtonOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  showBorder?: boolean;
  borderRadius?: string;
  layout?: ToggleButtonLayout;
  gap?: string;
  padding?: string;
  disableSlideAnimation?: boolean;
  settings?: Settings;
}

interface ContainerProps {
  disabled: boolean;
  showBorder: boolean;
  borderRadius: string;
  gap: string;
  padding: string;
  settings: Settings;
}

const Container = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      'disabled',
      'showBorder',
      'borderRadius',
      'gap',
      'padding',
      'settings',
    ].includes(prop as string),
})<ContainerProps>(
  ({disabled, showBorder, borderRadius, gap, padding, settings}) => ({
    position: 'relative',
    display: 'flex',
    background:
      settings.backgroundColor ||
      (showBorder ? designSystemToken('semantic.bg.secondary') : 'transparent'),
    borderRadius: (() => {
      if (showBorder) {
        return borderRadius;
      }

      return settings.borderRadius || '16px';
    })(),
    border: showBorder
      ? `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`
      : 'none',
    width: '100%',
    minHeight: '48px',
    overflow: 'hidden',
    opacity: disabled ? 0.5 : 1,
    gap,
    padding,
    alignItems: 'center',
    boxSizing: 'border-box',
  })
);

interface OptionButtonProps {
  selected?: boolean;
  layout: ToggleButtonLayout;
  option: ToggleButtonOption;
  showBorder: boolean;
  settings: Settings;
}

const OptionButton = styled(Box, {
  shouldForwardProp: (prop) =>
    !['selected', 'layout', 'option', 'showBorder', 'settings'].includes(
      prop as string
    ),
})<OptionButtonProps>(({selected, layout, option, showBorder, settings}) => {
  const getFlexStyles = () => {
    switch (layout) {
      case 'equal':
        return {flex: 1};
      case 'content':
        return {flex: 'none', width: 'auto'};
      case 'custom':
        return {
          flex: option.flex || 'none',
          minWidth: option.minWidth || 'auto',
        };
      default:
        return {flex: 1};
    }
  };

  return {
    ...getFlexStyles(),
    zIndex: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: showBorder ? '8px 0' : '6px 12px',
    gap: option.icon ? '8px' : '0',
    fontFamily: 'Brown LL, sans-serif',
    fontWeight: selected ? 500 : 400,
    fontSize: '14px',
    lineHeight: '18px',
    color: designSystemToken('semantic.fg.primary'),
    transition: 'font-weight 0.2s',
    userSelect: 'none',
    borderRadius: (() => {
      if (!showBorder) {
        return settings.optionBorderRadius || '12px';
      }

      return settings.optionBorderRadius || '0';
    })(),
    backgroundColor: (() => {
      if (!showBorder && selected && !settings.selectedOptionColor) {
        return designSystemToken('semantic.bg.tertiary');
      }
      if (selected && settings.selectedOptionColor) {
        return settings.selectedOptionColor;
      }

      return settings.optionColor || 'transparent';
    })(),
    minHeight: !showBorder ? '32px' : undefined,
  };
});

const shouldNotForwardProps = ['enableTransition'];

const Slide = styled(Box, {
  shouldForwardProp: (prop) => !shouldNotForwardProps.includes(prop as string),
})<{left: number; width: number; enableTransition: boolean}>(
  ({left, width, enableTransition}) => ({
    position: 'absolute',
    top: 4,
    left: left + 4,
    width: width - 8,
    height: 'calc(100% - 10px)',
    background: designSystemToken('semantic.bg.tertiary'),
    border: `1px solid ${designSystemToken('semantic.fg.primary')}`,
    borderRadius: '12px',
    boxShadow: '0 1px 4px 0 rgba(44,45,48,0.01)',
    transition: enableTransition
      ? 'left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1)'
      : 'none',
    zIndex: 0,
  })
);

function ToggleButton({
  testID = 'toggle-button',
  options,
  value,
  onChange,
  disabled = false,
  showBorder = true,
  borderRadius = '16px',
  layout = 'equal',
  gap = '0',
  padding = '0',
  disableSlideAnimation = false,
  settings = {},
}: ToggleButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [slideStyle, setSlideStyle] = useState({left: 0, width: 0});
  const [hasMeasured, setHasMeasured] = useState(false);
  const [enableTransition, setEnableTransition] = useState(false);
  const mergedSettings = useMemo(
    () => ({...DEFAULT_SETTINGS, ...settings}),
    [settings]
  );

  useLayoutEffect(() => {
    if (disableSlideAnimation || !showBorder || !containerRef.current) {
      return;
    }
    const idx = options.findIndex((opt) => opt.value === value);
    if (idx === -1) {
      return;
    }
    const {children} = containerRef.current;
    // Slide is not rendered on first pass, so children[idx] is the option
    // On subsequent passes, Slide is first, so children[idx + 1] is the option
    const optionEl = hasMeasured
      ? (children[idx + 1] as HTMLElement)
      : (children[idx] as HTMLElement);
    if (optionEl) {
      setSlideStyle({
        left: optionEl.offsetLeft,
        width: optionEl.offsetWidth,
      });
      if (!hasMeasured) {
        setHasMeasured(true);
        // Enable transition after the first measurement/render
        setTimeout(() => setEnableTransition(true), 0);
      }
    }
  }, [disableSlideAnimation, showBorder, value, options, hasMeasured]);

  const getOptionContent = useCallback(
    (option: ToggleButtonOption, selected: boolean) => {
      let textElement;
      if (showBorder) {
        textElement = selected ? (
          <Label size="md" color={designSystemToken('semantic.fg.primary')}>
            {option.label}
          </Label>
        ) : (
          <Body size="md" color={designSystemToken('semantic.fg.primary')}>
            {option.label}
          </Body>
        );
      } else {
        textElement = <span>{option.label}</span>;
      }

      if (option.icon) {
        return (
          <>
            {option.icon}
            {textElement}
          </>
        );
      }

      return textElement;
    },
    [showBorder]
  );

  return (
    <Container
      ref={containerRef}
      data-testid={testID}
      disabled={disabled}
      showBorder={showBorder}
      borderRadius={borderRadius}
      gap={gap}
      padding={padding}
      settings={mergedSettings}
    >
      {!disableSlideAnimation && showBorder && hasMeasured && (
        <Slide
          left={slideStyle.left}
          width={slideStyle.width}
          enableTransition={enableTransition}
        />
      )}
      {options.map((option) => (
        <OptionButton
          key={option.value}
          selected={option.value === value}
          layout={layout}
          option={option}
          showBorder={showBorder}
          onClick={() => {
            if (!disabled) {
              onChange(option.value);
            }
          }}
          aria-pressed={option.value === value}
          tabIndex={disabled ? -1 : 0}
          role="button"
          sx={{pointerEvents: disabled ? 'none' : 'auto'}}
          settings={mergedSettings}
        >
          {getOptionContent(option, option.value === value)}
        </OptionButton>
      ))}
    </Container>
  );
}

export default ToggleButton;
